import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useFormikContext } from 'formik';
import styled from 'styled-components';
import { Button } from '@rtkwlf/ui';
import { ROUTE_PATH } from '../../constants';
import {
  ContactFormValues,
  useConfirmationRequiredRef,
} from './contacts/v2/constants';

const FooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

const SubmissionContainer = styled.div`
  display: flex;
`;

const SaveButton: typeof Button = styled(Button)`
  margin-left: 1rem;
`;

const useCancelConfirmation = (onCancel: () => void) => {
  const confirmationRequired = useConfirmationRequiredRef();

  return React.useCallback(() => {
    if (
      !confirmationRequired.current ||
      window.confirm(
        'All newly entered data will be lost. Are you sure you want to close?',
      )
    ) {
      onCancel();
    }
  }, [confirmationRequired, onCancel]);
};

type Props = {
  showSaveAndAdd: boolean;
  view?: string;
};

export const FooterButtons: React.FunctionComponent<Props> = ({
  showSaveAndAdd,
  view = '',
}) => {
  const history = useHistory();

  const goBack = React.useCallback(
    () =>
      history.push({
        pathname: ROUTE_PATH.ONBOARDING.MAIN,
        search: `view=${view}`,
      }),
    [history, view],
  );

  // need to use both setIsSaving and setSubmitting to prevent a limbo state
  // because submitForm resets isSubmitting to false
  const [isSaving, setIsSaving] = React.useState<boolean>(false);

  const onCancel = useCancelConfirmation(goBack);

  const { submitForm, resetForm, isValid, dirty, setSubmitting } =
    useFormikContext<ContactFormValues>();

  const isSaveDisabled = !dirty || !isValid;

  const onSave = React.useCallback(async () => {
    setIsSaving(true);
    await submitForm();
    setIsSaving(false);
    setSubmitting(true); // to prevent router prompt to trigger
    goBack();
    setSubmitting(false);
  }, [submitForm, goBack, setSubmitting]);

  const onSaveAndAdd = React.useCallback(async () => {
    setIsSaving(true);
    await submitForm();
    setIsSaving(false);
    setSubmitting(true); // to prevent router prompt to trigger
    resetForm();
    setSubmitting(false);
  }, [submitForm, resetForm, setSubmitting]);

  return (
    <FooterContainer>
      <Button
        type="primary"
        htmlType="button"
        ghost
        onClick={onCancel}
        data-cy="cancel"
      >
        Cancel
      </Button>
      <SubmissionContainer>
        {showSaveAndAdd && (
          <Button
            type="primary"
            htmlType="button"
            ghost
            onClick={onSaveAndAdd}
            disabled={isSaveDisabled || isSaving}
            data-cy="save-add"
          >
            {isSaving ? 'Saving...' : 'Save & Add'}
          </Button>
        )}
        <SaveButton
          type="primary"
          htmlType="button"
          onClick={onSave}
          disabled={isSaveDisabled || isSaving}
          data-cy="save"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </SaveButton>
      </SubmissionContainer>
    </FooterContainer>
  );
};
